import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

interface PersistentLinkProps extends React.ComponentProps<typeof RouterLink> {
    to: string;
    children: React.ReactNode;
}

/**
 * A wrapper around react-router's Link that preserves query parameters across navigations.
 * Use this instead of RouterLink when you want to preserve params like ?newStack=true.
 */
const PersistentLink: React.FC<PersistentLinkProps> = ({ to, children, ...props }) => {
    const location = useLocation();

    // Merge current search params with destination
    const currentParams = new URLSearchParams(location.search);
    const targetUrl = new URL(to, 'http://dummy.com');
    const targetParams = new URLSearchParams(targetUrl.search);

    // List of transient params that should NOT persist to new routes
    const transientParams = ['case_id', 'from', 'tab', 'page', 'rowsPerPage', 'userType', 'hideInternal', 'success', 'type'];

    // Remove transient params when navigating to a different path
    if (targetUrl.pathname !== location.pathname) {
        transientParams.forEach(param => {
            if (!targetParams.has(param)) {
                currentParams.delete(param);
            }
        });
    }

    // Merge: target params override current params
    targetParams.forEach((value, key) => {
        currentParams.set(key, value);
    });

    const finalTo = {
        pathname: targetUrl.pathname,
        search: currentParams.toString() ? `?${currentParams.toString()}` : '',
        hash: targetUrl.hash
    };

    return (
        <RouterLink to={finalTo} {...props}>
            {children}
        </RouterLink>
    );
};

export default PersistentLink;
